import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { getStoredSignupUtm } from '@/modules/attribution/attribution';

const SYNCED_KEY = 'lureo_attribution_synced';

/**
 * Persists first-touch UTM (from localStorage) to the user's org_member once they are
 * authenticated. One mechanism covers all cases: going-forward email + OAuth signups
 * (fires on the first authenticated load after signup) and backfilling existing users
 * (fires on their next visit). The backend write is set-once — it fills only null
 * columns — so this is idempotent and first-touch always wins.
 *
 * Deliberately NOT gated on cookie/analytics consent: this is a first-party account
 * write, not client-side analytics tracking, so it also captures users who declined the
 * analytics cookie (whom PostHog never sees).
 */
export function AttributionSync() {
  const { user } = useAuth();
  const services = useServices();
  const attempted = useRef(false);

  useEffect(() => {
    if (!user?.id || attempted.current) return;

    let alreadySynced = false;
    try {
      alreadySynced = localStorage.getItem(SYNCED_KEY) === '1';
    } catch {
      // localStorage blocked — treat as not synced; the backend stays idempotent.
    }
    if (alreadySynced) {
      attempted.current = true;
      return;
    }

    const { utmSource, utmCampaign } = getStoredSignupUtm();
    if (!utmSource && !utmCampaign) return;

    attempted.current = true;
    services.auth
      .setSignupAttribution({ utmSource, utmCampaign })
      .then(() => {
        try {
          localStorage.setItem(SYNCED_KEY, '1');
        } catch {
          // best-effort; backend set-once keeps a later retry harmless
        }
      })
      .catch(() => {
        // setSignupAttribution reports its own failures to Sentry; allow a retry next load.
        attempted.current = false;
      });
  }, [user?.id, services]);

  return null;
}
